/**
 * Ink demand forecast — the shared model.
 *
 * Both screens read this file and nothing else for their arithmetic. That is the
 * point: the interactive report and the printed order document have to be
 * incapable of disagreeing, and the only way to guarantee that is for the paper
 * to be a different rendering of the same numbers rather than a second
 * implementation of the same formulas.
 *
 * THE ADDITIVITY GUARANTEE
 *
 * Every level of the row hierarchy and every column grain is a plain sum over
 * leaf months. Collapsing a row or a quarter changes what you SEE, never what it
 * sums to — the Σ in the status bar must not move. `aggregate()` is the one
 * function behind months, quarters and the order window, so there is no second
 * path where that could drift.
 *
 * kg IS DERIVED HERE, NOT SENT
 *
 * The API sends sheets (inherited verbatim from the Prism board forecast) plus
 * g per 1000 sheets. kg = sheets x g/1000 / 1e6. Sheet demand is NOT recomputed
 * anywhere in this app — the ink report and the board report have to reconcile.
 */

export const HORIZON = 12;

// ---------------------------------------------------------------------------
// Wire types — the shape backend/helpers/ink_forecast.py returns.
// ---------------------------------------------------------------------------

export interface ForecastLeaf {
  code: string;                 // FG SKU
  name: string;                 // its description
  board: string;                // RM / board code the sheets are printed on
  print_unit: number | null;    // press unit this ink runs on for this SKU
  coverage_pct: number;
  /** g of ink film per m² at 100% coverage: 1.2 process/ECG, 1.5 spot, or a
   *  calibrated per-SKU override. The reason two inks at the same coverage on
   *  the same sheet do not cost the same kg. */
  mileage_g_per_m2: number | null;
  /** True when a saved per-SKU rate was used instead of the class default —
   *  a measured figure deserves different trust from an assumed one. */
  mileage_overridden: boolean;
  g_per_1000_sheets: number;
  separation: string;           // the artwork separation this leaf costs
  sheets: number[];             // 12, positional
}

export interface ForecastForm {
  code: string;                 // form / die
  skus: ForecastLeaf[];
}

export interface ForecastCustomer {
  code: string;
  name: string;
  forms: ForecastForm[];
}

export interface ForecastInk {
  ink_key: string;
  description: string;
  colour_name: string | null;
  series_suffix: string | null;
  series_label: string | null;
  hex_colour: string | null;
  supplier_code: string | null;
  assigned: boolean;            // false = no ink SKU on file; can't be ordered
  moq_kg: number | null;        // pack size = the order multiple
  opening_kg: number;
  delivery: number[];           // 12, scheduled inbound kg
  customers: ForecastCustomer[];
}

export interface ForecastSeries {
  code: string;                 // PROCESS | PANTONE | COATINGS | SPECIALS | UNASSIGNED
  name: string;
  inks: ForecastInk[];
}

export interface ForecastProjection {
  id: number;
  code: string;
  customer: string;
  description: string | null;
  source_file: string | null;
  imported_at: string | null;
  start_month: string | null;
  /** Demand this projection holds for months closing before the shared horizon
   *  opens — already consumed, and excluded from every total on the report. */
  dropped_sheets_before_horizon: number;
}

export type ExclusionKind =
  | 'artwork_missing' | 'no_artwork_path' | 'no_separations' | 'incomplete'
  | 'not_sheet_fed' | 'no_master' | 'other';

export interface ForecastExclusion {
  sku: string;
  customer: string;
  description: string | null;
  board: string;
  form: string | null;
  sheets: number;
  /** Machine-readable cause — the view groups on this, never on the prose. */
  kind: ExclusionKind;
  reason: string;
}

/** A line whose demand DOES count but which is missing an identity field the
 *  category master should have supplied. A work queue, not a lost number. */
export interface MasterGap {
  sku: string;
  customer: string;
  field: string;
  board: string;
  sheets: number;
  reason: string;
}

export interface InkForecast {
  /** Every projection the figures were built from. Ink is many-to-many, so a
   *  usable order quantity needs every customer's demand in the total — this is
   *  normally the newest projection for each customer. */
  projections: ForecastProjection[];
  customers: string[];
  /** First day of the shared horizon: the LATEST projection month in the set. */
  horizon_start: string | null;
  months: string[];             // 12 labels on the shared calendar horizon
  series: ForecastSeries[];
  excluded: ForecastExclusion[];
  master_gaps: MasterGap[];
  sheet_totals: {
    sheets_by_month: number[];
    months: string[];
    verified_at: string;
  };
  mileage: { process_g_per_m2: number; spot_g_per_m2: number };
}

// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------

/** A month array of zeroes — the identity every roll-up starts from. */
export const zeroes = (): number[] => new Array(HORIZON).fill(0);

/** kg per month for one leaf. The only place sheets become kg. */
export function leafKg(leaf: ForecastLeaf): number[] {
  const out = zeroes();
  for (let i = 0; i < HORIZON; i += 1) {
    out[i] = ((leaf.sheets[i] || 0) * leaf.g_per_1000_sheets) / 1e6;
  }
  return out;
}

export function addInto(target: number[], source: number[]): number[] {
  for (let i = 0; i < HORIZON; i += 1) target[i] += source[i] || 0;
  return target;
}

/**
 * Sum a month array over a column's month indices.
 *
 * ONE function serves monthly columns (one index), quarters (three) and the
 * order window (any contiguous run). A second implementation for any of them is
 * how a total starts disagreeing with itself.
 */
export function aggregate(values: number[], indices: number[]): number {
  let total = 0;
  for (const i of indices) total += values[i] || 0;
  return total;
}

/**
 * Running projected balance: opening + delivery − demand, carried forward.
 * Signed — a negative month is the whole reason a buyer opens this report.
 */
export function balanceOf(opening: number, delivery: number[], demand: number[]): number[] {
  const out: number[] = [];
  let run = opening;
  for (let i = 0; i < HORIZON; i += 1) {
    run = run + (delivery[i] || 0) - (demand[i] || 0);
    out.push(run);
  }
  return out;
}

/**
 * Order quantity for a window: net of opening stock, rounded UP to the pack.
 *
 * Rounding up is not a nicety — you cannot buy a fraction of a tub, so the
 * orderable figure is always the next whole pack. An ink with no pack size on
 * file falls back to 1 kg rather than dividing by zero; the document shows the
 * missing MOQ so it reads as a master-data gap, not a rounded number.
 */
export function orderKg(demand: number, opening: number, moq: number | null): number {
  const net = Math.max(0, demand - opening);
  if (net <= 0) return 0;
  const pack = moq && moq > 0 ? moq : 1;
  return Math.ceil(net / pack) * pack;
}

// ---------------------------------------------------------------------------
// The tree, with roll-ups computed once
// ---------------------------------------------------------------------------

export type NodeLevel = 0 | 1 | 2 | 3 | 4;

export interface ForecastNode {
  /**
   * Stable BUSINESS key — `i:CHECO|c:NESB|f:F1163B`, not a synthetic row id.
   * Shared links carry these, so a link has to survive tomorrow's re-import of
   * the forecast. A positional id would die overnight and defeat the purpose.
   */
  key: string;
  level: NodeLevel;
  code: string;
  name: string;
  kg: number[];
  sheets: number[];
  leafCount: number;
  children: ForecastNode[];
  ink?: ForecastInk;            // level 1 only
  leaf?: ForecastLeaf;          // level 4 only
  meta?: string;                // level 1 and 4: the derivation line
}

/**
 * The derivation line under every leaf — board, press unit, coverage, mileage.
 *
 * Kept in this order and no longer: it is what turns the kg above it from an
 * assertion into a working, and the label column ellipsises, so anything
 * appended past `g/1000 sh` costs the reader the very figure the row exists for.
 * The separation name is carried separately (see leafFullMeta) for the case
 * where one SKU hits the same ink twice.
 */
/**
 * The derivation line under a leaf: the board, and the mileage that turns its
 * sheets into kg. Nothing else.
 *
 * Print unit is deliberately absent — it says which press unit the ink runs on,
 * which is a make-ready fact, not part of how this number was arrived at. It
 * stays available on the row's hover title (leafFullMeta) for anyone who wants
 * it. Coverage moved up to the FG SKU row; see leafCoverage.
 *
 * The g/m² rate IS part of the working and sits between the two, in the order the
 * arithmetic runs: sheet area (from the board code) x this rate x coverage. It
 * earns the width because process and spot are costed at different rates (1.2 vs
 * 1.5), so two inks at identical coverage on the same sheet legitimately differ
 * in kg — and without the rate on the row there is nothing to explain that. A
 * trailing * marks a calibrated per-SKU override rather than the class default;
 * one character, because the distinction matters more than its explanation, which
 * is in the hover title.
 */
export const leafMileage = (leaf: ForecastLeaf): string | null => (
  leaf.mileage_g_per_m2 == null ? null
    : `${leaf.mileage_g_per_m2} g/m²${leaf.mileage_overridden ? '*' : ''}`
);

const leafMeta = (leaf: ForecastLeaf): string => {
  const mileage = leafMileage(leaf);
  return [
    `RM: ${leaf.board}`,
    ...(mileage ? [mileage] : []),
    `${Math.round(leaf.g_per_1000_sheets)} g/1000 sh`,
  ].join(' · ');
};

/**
 * Coverage sits on the FG SKU row, not on the sheet-demand line beneath it.
 * It is the property of the artwork that a planner scans down a column looking
 * for — a heavy SKU is the reason an ink is short — so it belongs on the row
 * that carries the SKU's identity, where it can be compared leaf to leaf,
 * rather than buried mid-string in the derivation line.
 */
export const leafCoverage = (leaf: ForecastLeaf): string =>
  `${leaf.coverage_pct.toFixed(1)}%`;

/** The full derivation, for the hover title where nothing is clipped. */
export const leafFullMeta = (leaf: ForecastLeaf): string => {
  const bits = [leafMeta(leaf), `Coverage ${leafCoverage(leaf)}`];
  // Spell out what the bare rate (and its *) means, here where there is room.
  if (leaf.mileage_g_per_m2 != null) {
    bits.push(`Mileage ${leaf.mileage_g_per_m2} g/m² at solid — ${
      leaf.mileage_overridden
        ? 'calibrated rate saved for this SKU'
        : 'standard rate for this colour class'}`);
  }
  if (leaf.print_unit != null) bits.push(`Unit ${leaf.print_unit}`);
  bits.push(`separation ${leaf.separation}`, leaf.name);
  return bits.join(' · ');
};

const inkMeta = (ink: ForecastInk): string => {
  const bits: string[] = [];
  if (ink.series_label) bits.push(ink.series_label);
  bits.push(ink.moq_kg ? `pack ${ink.moq_kg} kg` : 'pack size not on file');
  if (!ink.assigned) bits.push('NOT ORDERABLE');
  return bits.join(' · ');
};

/**
 * Build the node tree with every roll-up already summed.
 *
 * Done once per forecast rather than per render: the report re-renders on every
 * expand, every column change and every drag across the month headers, and
 * re-summing 800+ leaves through five levels on each of those is the difference
 * between an instant report and a laggy one.
 */
export function buildTree(forecast: InkForecast): ForecastNode[] {
  return forecast.series.map((series) => {
    const seriesNode: ForecastNode = {
      key: `s:${series.code}`, level: 0, code: series.code, name: series.name,
      kg: zeroes(), sheets: zeroes(), leafCount: 0, children: [],
    };

    seriesNode.children = series.inks.map((ink) => {
      const inkNode: ForecastNode = {
        key: `i:${ink.ink_key}`, level: 1, code: ink.ink_key,
        name: ink.description, kg: zeroes(), sheets: zeroes(), leafCount: 0,
        children: [], ink, meta: inkMeta(ink),
      };

      inkNode.children = ink.customers.map((cust) => {
        const custNode: ForecastNode = {
          key: `${inkNode.key}|c:${cust.code}`, level: 2, code: cust.code,
          name: cust.name, kg: zeroes(), sheets: zeroes(), leafCount: 0, children: [],
        };

        custNode.children = cust.forms.map((form) => {
          const formNode: ForecastNode = {
            key: `${custNode.key}|f:${form.code}`, level: 3, code: form.code,
            name: 'form / die', kg: zeroes(), sheets: zeroes(), leafCount: 0, children: [],
          };

          formNode.children = form.skus.map((leaf, i) => {
            const kg = leafKg(leaf);
            const node: ForecastNode = {
              // A SKU can carry two separations of the same ink (a duplex hit),
              // so the separation is part of the key — without it the two rows
              // would collapse into one in the open-set and toggle together.
              key: `${formNode.key}|k:${leaf.code}:${leaf.separation}:${i}`,
              level: 4, code: leaf.code, name: leaf.name, kg, sheets: leaf.sheets,
              leafCount: 1, children: [], leaf, meta: leafMeta(leaf),
            };
            addInto(formNode.kg, kg);
            addInto(formNode.sheets, leaf.sheets);
            formNode.leafCount += 1;
            return node;
          });

          addInto(custNode.kg, formNode.kg);
          addInto(custNode.sheets, formNode.sheets);
          custNode.leafCount += formNode.leafCount;
          return formNode;
        });

        addInto(inkNode.kg, custNode.kg);
        addInto(inkNode.sheets, custNode.sheets);
        inkNode.leafCount += custNode.leafCount;
        return custNode;
      });

      addInto(seriesNode.kg, inkNode.kg);
      addInto(seriesNode.sheets, inkNode.sheets);
      seriesNode.leafCount += inkNode.leafCount;
      return inkNode;
    });

    return seriesNode;
  });
}

/** Every ink node in the tree, series order preserved. */
export function allInkNodes(tree: ForecastNode[]): ForecastNode[] {
  return tree.flatMap((s) => s.children);
}

/** Total kg across every ink and every month — the additivity guarantee's subject. */
export function grandTotalKg(tree: ForecastNode[]): number {
  let total = 0;
  for (const series of tree) for (const v of series.kg) total += v;
  return total;
}

/** Inks whose projected balance dips below zero at any point in the horizon. */
export function shortInks(tree: ForecastNode[]): ForecastNode[] {
  return allInkNodes(tree).filter((node) => {
    const ink = node.ink!;
    return balanceOf(ink.opening_kg, ink.delivery, node.kg).some((v) => v < 0);
  });
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

export type ColMode = 'month' | 'quarter';

export interface ForecastColumn {
  key: string;
  label: string;
  sub: string;
  indices: number[];
  /** Wide columns (the order window and quarter totals) get their own fill and rule. */
  wide: boolean;
  /** Set on a month column — only months are drag targets for the window. */
  month?: number;
  /** Set on a quarter total — carries the +mth / -mth toggle. */
  quarter?: number;
}

export const shortMonth = (label: string): string => (label || '').slice(0, 3);

/**
 * The column set for a given grain and window.
 *
 * The order window is always present and sits BESIDE the monthly detail rather
 * than replacing it — you should be able to read the order quantity and the
 * shape of the demand behind it without switching views. It is placed as a
 * subtotal IN PLACE, immediately after the last column it fully covers.
 */
export function buildColumns(
  months: string[], colMode: ColMode, winStart: number, winEnd: number,
  qOpen: Record<number, boolean>,
): ForecastColumn[] {
  const out: ForecastColumn[] = [];
  const winIdx: number[] = [];
  for (let i = winStart; i <= winEnd; i += 1) winIdx.push(i);

  if (colMode === 'quarter') {
    for (let q = 0; q < 4; q += 1) {
      const idx = [q * 3, q * 3 + 1, q * 3 + 2];
      // Opened months sit to the LEFT of their quarter total, in place, so the
      // quarter still reads as the sum of what precedes it.
      if (qOpen[q]) {
        idx.forEach((i) => out.push({
          key: `m${i}`, label: shortMonth(months[i]), sub: '', indices: [i],
          wide: false, month: i,
        }));
      }
      out.push({
        key: `q${q}`, label: `Q${q + 1}`,
        sub: `${shortMonth(months[q * 3])}–${shortMonth(months[q * 3 + 2])}`,
        indices: idx, wide: true, quarter: q,
      });
    }
  } else {
    for (let i = 0; i < HORIZON; i += 1) {
      out.push({
        key: `m${i}`, label: shortMonth(months[i]), sub: '', indices: [i],
        wide: false, month: i,
      });
    }
  }

  // The order window is a SUBTOTAL IN PLACE: it sits immediately after the last
  // column it fully covers, so a 3-month window lands in column 4 and a 6-month
  // one in column 7. You read the months it totals, hit the total, and carry on
  // into the months beyond it — which is how a subtotal is read anywhere else.
  //
  // "Fully covers" rather than "contains winEnd": a column that runs past the
  // window must come AFTER the total, or the total appears to include it. At the
  // 3/6/12 presets the two rules agree; they differ only for a window dragged to
  // an odd boundary in quarterly mode, and this is the reading that cannot
  // mislead. A 12-month window naturally lands last, which is correct.
  const windowCol: ForecastColumn = {
    key: 'w',
    label: `${shortMonth(months[winStart])}–${shortMonth(months[winEnd])}`,
    sub: `${winIdx.length} MTH ORDER`,
    indices: winIdx, wide: true,
  };
  let at = 0;
  out.forEach((c, i) => {
    if (Math.max(...c.indices) <= winEnd) at = i + 1;
  });
  out.splice(at, 0, windowCol);
  return out;
}

// ---------------------------------------------------------------------------
// Shareable state
// ---------------------------------------------------------------------------

export interface ShareState {
  colMode: ColMode;
  winStart: number;
  winEnd: number;
  open: Record<string, boolean>;
  qOpen: Record<number, boolean>;
  /** "CODE:CUSTOMER" pairs. undefined = the server default (newest per
   *  customer, every customer) — which is NOT the same as an empty array. */
  projections?: string[];
}

/**
 * `#c=month&w=0-5&o=<key>.<key>&q=0.2`
 *
 * Row keys are business keys (see ForecastNode.key) so a shared link still opens
 * the same branches after tomorrow's re-import. They contain '|' and ':', so
 * each is URI-encoded before joining on '.'.
 */
export function encodeShareState(s: ShareState): string {
  const open = Object.keys(s.open).filter((k) => s.open[k]);
  const qs = Object.keys(s.qOpen).filter((k) => s.qOpen[Number(k)]);
  return [
    `c=${s.colMode}`,
    `w=${s.winStart}-${s.winEnd}`,
    open.length ? `o=${open.map(encodeURIComponent).join('.')}` : '',
    qs.length ? `q=${qs.join('.')}` : '',
    // An explicit selection is carried; the default is left absent so a shared
    // link keeps following "newest per customer" rather than pinning today's
    // projections into someone else's tomorrow.
    s.projections ? `p=${encodeURIComponent(s.projections.join(','))}` : '',
  ].filter(Boolean).join('&');
}

export function decodeShareState(hash: string): Partial<ShareState> {
  const raw = (hash || '').replace(/^#/, '');
  if (!raw) return {};
  const q: Record<string, string> = {};
  raw.split('&').forEach((part) => {
    const eq = part.indexOf('=');
    if (eq > 0) q[part.slice(0, eq)] = part.slice(eq + 1);
  });
  const out: Partial<ShareState> = {};
  if (q.c === 'month' || q.c === 'quarter') out.colMode = q.c;
  if (q.w && /^\d+-\d+$/.test(q.w)) {
    const [a, b] = q.w.split('-').map(Number);
    const start = Math.min(Math.max(a, 0), HORIZON - 1);
    out.winStart = start;
    out.winEnd = Math.min(Math.max(b, start), HORIZON - 1);
  }
  if (q.o) {
    const m: Record<string, boolean> = {};
    q.o.split('.').forEach((k) => { if (k) m[decodeURIComponent(k)] = true; });
    out.open = m;
  }
  if (q.q) {
    const m: Record<number, boolean> = {};
    q.q.split('.').forEach((x) => { if (x !== '') m[Number(x)] = true; });
    out.qOpen = m;
  }
  if (q.p !== undefined) {
    // A present-but-empty p= is a real state: "no customers selected". Only an
    // ABSENT p= means "use the default".
    out.projections = decodeURIComponent(q.p).split(',').filter(Boolean);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/**
 * Zero renders as an em dash, never '0'. Most cells in a 12-month grid are
 * empty, and a field of zeroes hides the figures that matter inside it.
 */
export const fmtKg = (v: number): string => {
  if (!v) return '—';
  const a = Math.abs(v);
  const s = a >= 1000 ? Math.round(a).toLocaleString() : a.toFixed(1);
  return v < 0 ? `-${s}` : s;
};

export const fmtSheets = (v: number): string => (!v ? '—' : Math.round(v).toLocaleString());

// ---------------------------------------------------------------------------
// The buyer's document
// ---------------------------------------------------------------------------

export interface OrderLine {
  series: string;
  ink: ForecastInk;
  /** Total demand across the chosen window. */
  demandKg: number;
  /** Lowest projected balance up to the END of the window — not just at it. */
  minBalanceKg: number;
  leafCount: number;
  orderKg: number;
}

/**
 * One line per ink code, for the whole ink master — INCLUDING inks that need no
 * order.
 *
 * That inclusion is the design: the buyer works one document per projection
 * rather than wondering what a filter removed. It also inherits ONLY the window;
 * whatever the planner had expanded on screen has no effect on it whatsoever,
 * which is what makes it a document rather than a screenshot.
 */
export function buildOrderLines(
  tree: ForecastNode[], winStart: number, winEnd: number,
): OrderLine[] {
  const idx: number[] = [];
  for (let i = winStart; i <= winEnd; i += 1) idx.push(i);

  const out: OrderLine[] = [];
  for (const series of tree) {
    for (const node of series.children) {
      const ink = node.ink!;
      const demandKg = aggregate(node.kg, idx);
      const balance = balanceOf(ink.opening_kg, ink.delivery, node.kg);
      out.push({
        series: series.code,
        ink,
        demandKg,
        // The lowest point the balance reaches on the way to the window's end,
        // not the closing figure: an ink that runs out in month 2 and is
        // replenished in month 4 still stopped a press in month 2.
        minBalanceKg: Math.min(...balance.slice(0, winEnd + 1)),
        leafCount: node.leafCount,
        orderKg: orderKg(demandKg, ink.opening_kg, ink.moq_kg),
      });
    }
  }
  return out;
}


// ---------------------------------------------------------------------------
// Coverage — what the report could NOT cost
// ---------------------------------------------------------------------------

/**
 * Whether a gap is ours to close, or is correct and permanent.
 *
 * The distinction is the whole point of splitting these out. "183 SKUs not
 * costed" invites someone to shrug; "12.3M sheets of demand missing because
 * artwork has not been warmed" is a job, and "130k sheets of reel stock, which
 * this report will never cost" is a fact to stop worrying about.
 */
export type GapNature = 'fixable' | 'permanent';

export interface CoverageBucket {
  kind: ExclusionKind;
  label: string;
  nature: GapNature;
  /** What to do about it, or why there is nothing to do. */
  action: string;
  lines: number;
  sheets: number;
}

export interface Coverage {
  /** Every sheet in the selected projections — costed or not. */
  totalSheets: number;
  costedSheets: number;
  uncostedSheets: number;
  /** Uncosted as a percentage of all demand. THE number. */
  uncostedPct: number;
  buckets: CoverageBucket[];
  /** Lines whose demand DOES count but which lack a master identity field. */
  masterGaps: number;
}

const GAP_META: Record<ExclusionKind, { label: string; nature: GapNature; action: string }> = {
  artwork_missing: {
    label: 'artwork not cached', nature: 'fixable',
    action: 'warm the artwork from the board — this demand returns once it is cached',
  },
  no_artwork_path: {
    label: 'no artwork path', nature: 'fixable',
    action: 'record a confirmed path in production.artwork_path_overrides',
  },
  no_separations: {
    label: 'no costable separations', nature: 'fixable',
    action: 'the cached artwork carries no ink separations — re-extract it',
  },
  incomplete: {
    label: 'incomplete inputs', nature: 'fixable',
    action: 'coverage or trim box missing from the artwork — re-extract it',
  },
  not_sheet_fed: {
    label: 'reel or roll stock', nature: 'permanent',
    action: 'RM Qty is in metres, not sheets — this report costs sheet-fed work only',
  },
  no_master: {
    label: 'not in the category master', nature: 'fixable',
    action: 'run the category-items sync so the SKU has an identity',
  },
  other: { label: 'other', nature: 'fixable', action: 'see the line reason' },
};

/**
 * What the report could not cost, and why.
 *
 * A forecast that silently omits a fifth of its own demand is worse than no
 * forecast, because it looks complete. This is the number that has to be on the
 * face of the page — not the SKU count, which conveys nothing about magnitude.
 */
export function coverageSummary(forecast: InkForecast): Coverage {
  const totalSheets = forecast.sheet_totals.sheets_by_month.reduce((a, b) => a + b, 0);

  const byKind: Partial<Record<ExclusionKind, CoverageBucket>> = {};
  let uncostedSheets = 0;
  forecast.excluded.forEach((e) => {
    const kind = (e.kind || 'other') as ExclusionKind;
    const meta = GAP_META[kind] ?? GAP_META.other;
    const b = byKind[kind] ?? { kind, ...meta, lines: 0, sheets: 0 };
    b.lines += 1;
    b.sheets += e.sheets;
    byKind[kind] = b;
    uncostedSheets += e.sheets;
  });

  return {
    totalSheets,
    costedSheets: totalSheets - uncostedSheets,
    uncostedSheets,
    uncostedPct: totalSheets ? (100 * uncostedSheets) / totalSheets : 0,
    // Heaviest first: the biggest hole is the one worth acting on.
    buckets: (Object.values(byKind) as CoverageBucket[])
      .sort((a, b) => b.sheets - a.sheets),
    masterGaps: forecast.master_gaps.length,
  };
}
